import React, { useState } from 'react';
import './GeoReportPreview.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { format } from 'date-fns';
import { GeoReportResponse } from '../../../../shared/models/geo-report/GeoReportResponse';
import { ChatbeatPdfService } from '../../../../shared/services/pdf/ChatbeatPdfService';
import GeoReportPdfDocument from '../geo-report-pdf/GeoReportPdf';

interface GeoReportPreviewProps {
  report: GeoReportResponse;
  // True when this cache copy matches the account's own earlier audit → the note is accurate.
  // Computed by the parent (which has the account's report list). Defaults to false.
  cachedFromOwnAudit?: boolean;
}

const getScoreColor = (score: number): string => {
  if (score >= 70) return 'high';
  if (score >= 40) return 'mid';
  return 'low';
};

const getScoreLabel = (score: number): string => {
  if (score >= 80) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Needs Improvement';
  return 'Critical';
};

/**
 * Minimal markdown-to-HTML converter with card-based layout.
 *
 * Sections (h2 headings) are wrapped in card containers. The first section
 * (h1 + Executive Summary) is treated as a cover/hero area.
 *
 * Table detection: a block is treated as a markdown table when it contains at least two
 * consecutive lines that start and end with `|`, with a separator row (`|---|`) between
 * the header row and the body rows.
 */
const markdownToHtml = (md: string): string => {
  // Strip internal verification-tier markers ("[verified-live]", "[inferred]", …) the LLM appended
  // to Action Plan items — internal metadata that must never surface in the client report. Mirrors
  // GeoReportPdf's stripVerificationTags; keep both in sync.
  const deTagged = md.replace(
    /[ \t]*\[\s*(?:verified[-\s]live|not[-\s]verified|inferred|rendered|archived)\s*\]/gi,
    ''
  );
  // HTML-escape the raw source first to prevent XSS
  const escaped = deTagged.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Normalize line endings
  const normalized = escaped.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Extract markdown tables using multiline regex before line-by-line processing.
  const tableRegex = /(?:^[ \t]*\|[^\n]*\|[ \t]*\n){3,}/gm;
  const tables: string[] = [];
  const PLACEHOLDER = '\x00TABLE_';

  const withPlaceholders = normalized.replace(tableRegex, (match) => {
    const lines = match
      .trim()
      .split('\n')
      .map((l: string) => l.trim());

    const sepIdx = lines.findIndex((l: string) => /^\|[-\s|:]+\|$/.test(l));
    if (sepIdx < 0) return match;

    let html = '<table>';
    let inBody = false;
    for (const tl of lines) {
      if (/^\|[-\s|:]+\|$/.test(tl)) {
        inBody = true;
        continue;
      }
      const cells = tl
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c: string) => c.trim());
      const tag = inBody ? 'td' : 'th';
      html += `<tr>${cells.map((c) => `<${tag}>${styleCellContent(c)}</${tag}>`).join('')}</tr>`;
    }
    html += '</table>';

    const idx = tables.length;
    tables.push(html);
    return `${PLACEHOLDER}${idx}\n`;
  });

  // Process remaining lines
  let result = withPlaceholders
    // Strip raw-markdown artifacts that would otherwise render literally in the client report:
    // inline-code backticks, markdown horizontal rules (---/***/___), and leading blockquote
    // markers (already escaped to &gt; above). Mirrors the PDF renderer's cleanup.
    .replace(/`/g, '')
    .replace(/^([-*_])\1{2,}[ \t]*$/gm, '')
    .replace(/^&gt;\s?/gm, '')
    // Score/percentage lines ("Overall GEO Score: 64%", "Current Schema Score: 29/100") render
    // as a progress bar — mirrors the PDF renderer's detectProgress so the web report shows the
    // same score bars. The line must end right after the number (no trailing text), matching the
    .replace(/^([^\n|<]+?):[ \t]*(\d{1,3})[ \t]*(?:%|\/[ \t]*100)[ \t]*$/gm, (_m, label, val) => {
      const v = Math.min(100, Math.max(0, parseInt(val, 10)));
      const cls = v >= 70 ? 'high' : v >= 40 ? 'mid' : 'low';
      return `<div class="report-progress report-progress--${cls}"><div class="report-progress__head"><span>${label.trim()}</span><span class="report-progress__value">${v}%</span></div><div class="report-progress__track"><span class="report-progress__fill" style="width:${v}%"></span></div></div>`;
    })
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '\x01SECTION_BREAK\x01<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hultd\x00\x01])(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '');

  // Inject tables back at placeholder positions
  result = result.replace(/\x00TABLE_(\d+)/g, (_match, idx) => tables[parseInt(idx, 10)] || '');

  // Style status indicators (emojis) in content
  result = styleStatusIndicators(result);

  // Wrap sections into cards
  result = wrapSectionsInCards(result);

  return result;
};

/**
 * Style cell content: detect status emojis and score patterns in table cells.
 */
const styleCellContent = (content: string): string => {
  // Strip backticks + markdown bold/italic first so badge detection works on clean text
  const styled = content
    .replace(/`/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1');

  // Whole-cell status detection — mirrors the PDF renderer's getStatusStyle: when the entire
  // cell is a single status token, render exactly one badge. This keeps the web report in sync
  // with the PDF (incl. the 4-state crawler vocabulary) and avoids badging words that appear
  // mid-sentence inside long Notes/Details cells.
  const plain = styled
    .replace(/&amp;#x2705;|✅/g, 'Yes')
    .replace(/&amp;#x274C;|❌/g, 'No')
    .replace(/&amp;#x26A0;️?|⚠️|⚠/g, 'Warning')
    .replace(/\s+/g, ' ')
    .trim();
  const lower = plain.toLowerCase();

  // explicit-allow / default-allow → green (a wildcard or named allow both mean "AI crawlers
  // can access the site"); blocked → red, partial → amber, unknown → neutral.
  if (/^(explicit-allow|default-allow|allowed(?: \(default\))?|not mentioned|ready|strong|yes|present|exists|found|detected|accessible)$/.test(lower)) {
    return `<span class="report-badge report-badge--success">${plain}</span>`;
  }
  if (/^(blocked|no|not found|missing|absent|denied|inaccessible|not implemented|weak)$/.test(lower)) {
    return `<span class="report-badge report-badge--error">${plain}</span>`;
  }
  if (/^(partial|warning|needs fix|limited|needs work|in progress|adequate)$/.test(lower)) {
    return `<span class="report-badge report-badge--warning">${plain}</span>`;
  }
  if (/^(unknown|n\/a|not verified)$/.test(lower)) {
    return `<span class="report-badge report-badge--neutral">${plain}</span>`;
  }

  // Not a pure status cell: keep inline emoji icons + severity labels.
  let out = styled
    .replace(/&amp;#x2705;|✅/g, '<span class="report-status report-status--success">✅</span>')
    .replace(/&amp;#x274C;|❌/g, '<span class="report-status report-status--error">❌</span>')
    .replace(/&amp;#x26A0;|⚠️/g, '<span class="report-status report-status--warning">⚠️</span>');
  out = out
    .replace(/\bCritical\b/g, '<span class="report-severity report-severity--critical">Critical</span>')
    .replace(/\bHigh\b/g, '<span class="report-severity report-severity--high">High</span>')
    .replace(/\bMedium\b/g, '<span class="report-severity report-severity--medium">Medium</span>')
    .replace(/\bLow\b/g, '<span class="report-severity report-severity--low">Low</span>');
  return out;
};

/**
 * Style emoji status indicators in body content.
 */
const styleStatusIndicators = (html: string): string => {
  return html
    .replace(/✅/g, '<span class="report-status report-status--success">✅</span>')
    .replace(/❌/g, '<span class="report-status report-status--error">❌</span>')
    .replace(/⚠️/g, '<span class="report-status report-status--warning">⚠️</span>');
};

/**
 * Wrap h2 sections into card containers.
 * Content before the first h2 (title + executive summary) gets a hero wrapper.
 */
const wrapSectionsInCards = (html: string): string => {
  const parts = html.split('\x01SECTION_BREAK\x01');

  if (parts.length <= 1) return html;

  // Skip the first part entirely — the cover section already shows title + score.
  // Remaining parts = cards (each starts with <h2>)
  const cards = parts.slice(1).map((section) => {
    return `<div class="report-card">${section}</div>`;
  });

  return cards.join('');
};

const GeoReportPreview: React.FC<GeoReportPreviewProps> = ({ report, cachedFromOwnAudit = false }) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadPdf = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const filename = `GEO-Report-${report.url
        .replace(/https?:\/\//g, '')
        .replace(/[^a-z0-9]/gi, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      const doc = <GeoReportPdfDocument report={report} />;
      await ChatbeatPdfService.generate(doc, filename);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const htmlContent = report.reportMarkdown ? markdownToHtml(report.reportMarkdown) : '';
  const score = report.geoScore ?? 0;
  const scoreColor = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);

  return (
    <div className="geo-report-preview">
      <div className="geo-report-preview__actions">
        <Button id="geo-report-preview-download" className="action-btn" onClick={handleDownloadPdf} disabled={isExporting}>
          {isExporting ? (
            <span className="ico--loader ico--xs" />
          ) : (
            <span className="ico--download ico--xs" />
          )}
        </Button>
        {!isExporting && (
          <CustomTooltip tooltipText="geoReport.downloadPdf" targetRef="geo-report-preview-download" />
        )}
      </div>

      {cachedFromOwnAudit && (
        <div className="geo-report-preview__cache-note">
          <span className="ico--info ico--xs" />
          <span>{t('geoReport.cachedResultNote')}</span>
        </div>
      )}

      {htmlContent ? (
        <div className="geo-report-preview__report">
          <div className={`geo-report-preview__cover geo-report-preview__cover--${scoreColor}`}>
            <div className="geo-report-preview__cover-label">GEO Audit</div>
            <div className="geo-report-preview__cover-url">{report.url.replace(/^https?:\/\//, '')}</div>
            <div className={`geo-report-preview__cover-score geo-report-preview__cover-score--${scoreColor}`}>
              {score}
            </div>
            <div className="geo-report-preview__cover-max">/100</div>
            <div className={`geo-report-preview__cover-status geo-report-preview__cover-status--${scoreColor}`}>
              {scoreLabel}
            </div>
          </div>
          <div className="geo-report-preview__content" dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      ) : (
        <p className="geo-report-preview__empty">{t('geoReport.noContent')}</p>
      )}
    </div>
  );
};

export { GeoReportPreview };
export default GeoReportPreview;
